import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeaderNav from '../../components/ui/PublicHeaderNav';
import EventHero from './components/EventHero';
import EventDescription from './components/EventDescription';
import VenueInfo from './components/VenueInfo';
import TicketTiers from './components/TicketTiers';
import UpgradesSection from './components/UpgradesSection';
import MerchandiseSection from './components/MerchandiseSection';
import StickyCart from './components/StickyCart';
import ShareModal from './components/ShareModal';
import Icon from '../../components/AppIcon';


const EventDetail = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCartVisible, setIsCartVisible] = useState(true);

  // Mock event data
  const eventData = {
    id: "evt_001",
    name: "Summer Music Festival 2025",
    date: "2025-07-15",
    time: "18:00",
    duration: "8 hours",
    capacity: 5000,
    ageLimit: "18+",
    isFeatured: true,
    heroImage: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1200&h=600&fit=crop",
    description: `Join us for the most anticipated music festival of the summer! Experience three stages of incredible live music featuring top artists from around the world.\n\nThis year's lineup includes headliners from multiple genres including rock, electronic, indie, and pop. With food trucks, art installations, and interactive experiences throughout the venue.\n\nDon't miss out on this unforgettable weekend of music, food, and community. Early bird tickets are selling fast!`,
    tags: ["Music", "Festival", "Outdoor", "Summer", "Live Performance"],
    venue: {
      name: "Riverside Park Amphitheater",
      address: "1234 Park Avenue",
      city: "Austin",
      state: "TX",
      zipCode: "73301",
      phone: "(512) 555-0123",
      website: "https://riversidepark.com",
      latitude: 30.2672,
      longitude: -97.7431,
      features: [
        "Outdoor Venue",
        "Full Bar Service",
        "Food Vendors",
        "Accessible Seating",
        "VIP Lounge",
        "Merchandise Booths"
      ],
      parking: "Free parking available on-site. Additional paid parking at nearby lots. Rideshare drop-off zone located at main entrance."
    }
  };

  const ticketData = [
    {
      id: "tkt_001",
      name: "General Admission",
      description: "Access to main festival grounds and all three stages",
      price: 89.99,
      originalPrice: null,
      available: 2000,
      sold: 1200,
      isPopular: true,
      benefits: [
        "Access to all stages",
        "Festival grounds access",
        "Food truck area"
      ]
    },
    {
      id: "tkt_002",
      name: "VIP Experience",
      description: "Premium viewing areas, complimentary drinks, and exclusive lounge access",
      price: 199.99,
      originalPrice: 249.99,
      available: 500,
      sold: 320,
      isPopular: false,
      benefits: [
        "Priority stage viewing",
        "VIP lounge access",
        "Complimentary drinks",
        "Exclusive merchandise",
        "Meet & greet opportunities"
      ]
    },
    {
      id: "tkt_003",
      name: "Student Discount",
      description: "Special pricing for students with valid ID",
      price: 69.99,
      originalPrice: 89.99,
      available: 300,
      sold: 280,
      isPopular: false,
      benefits: [
        "Access to all stages",
        "Student networking area",
        "Festival grounds access"
      ]
    }
  ];

  const upgradeData = [
    {
      id: "upg_001",
      name: "VIP Parking Pass",
      description: "Reserved parking spot close to main entrance",
      price: 25.00,
      available: 200,
      sold: 150,
      icon: "Car",
      features: [
        "Reserved parking spot",
        "5-minute walk to entrance",
        "Security monitored"
      ]
    },
    {
      id: "upg_002",
      name: "Fast Track Entry",
      description: "Skip the regular entry lines with priority access",
      price: 15.00,
      available: 500,
      sold: 400,
      icon: "Zap",
      features: [
        "Dedicated entry lane",
        "Skip waiting lines",
        "Faster security check"
      ]
    },
    {
      id: "upg_003",
      name: "Photo Package",
      description: "Professional photos from the event delivered digitally",
      price: 35.00,
      available: 100,
      sold: 95,
      icon: "Camera",
      features: [
        "Professional photography",
        "Digital delivery",
        "High-resolution images",
        "Social media ready"
      ]
    }
  ];

  const merchandiseData = [
    {
      id: "merch_001",
      name: "Festival T-Shirt",
      description: "Official festival t-shirt with 2025 lineup design",
      price: 29.99,
      image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=300&fit=crop",
      stock: 500,
      sizes: [
        { size: "S", stock: 50 },
        { size: "M", stock: 100 },
        { size: "L", stock: 150 },
        { size: "XL", stock: 100 },
        { size: "XXL", stock: 50 }
      ]
    },
    {
      id: "merch_002",
      name: "Festival Poster",
      description: "Limited edition poster featuring festival artwork",
      price: 19.99,
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=300&h=300&fit=crop",
      stock: 200,
      sizes: null
    },
    {
      id: "merch_003",
      name: "Festival Hoodie",
      description: "Comfortable hoodie with embroidered festival logo",
      price: 49.99,
      image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=300&fit=crop",
      stock: 300,
      sizes: [
        { size: "S", stock: 30 },
        { size: "M", stock: 80 },
        { size: "L", stock: 100 },
        { size: "XL", stock: 70 },
        { size: "XXL", stock: 20 }
      ]
    }
  ];

  const handleAddToCart = (item, quantityChange) => {
    setCartItems(prevItems => {
      const existingItemIndex = prevItems?.findIndex(cartItem => 
        cartItem?.id === item?.id && 
        cartItem?.type === item?.type &&
        (item?.selectedSize ? cartItem?.selectedSize === item?.selectedSize : true)
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...prevItems];
        const newQuantity = updatedItems?.[existingItemIndex]?.quantity + quantityChange;
        
        if (newQuantity <= 0) {
          updatedItems?.splice(existingItemIndex, 1);
        } else {
          updatedItems[existingItemIndex].quantity = newQuantity;
        }
        
        return updatedItems;
      } else if (quantityChange > 0) {
        return [...prevItems, {
          ...item,
          quantity: quantityChange,
          type: item?.type || 'ticket'
        }];
      }
      
      return prevItems;
    });
  };

  const handleCheckout = () => {
    // Store cart items in localStorage for checkout page
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
    localStorage.setItem('eventData', JSON.stringify(eventData));
    navigate('/checkout');
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const getTotalCartItems = () => {
    return cartItems?.reduce((total, item) => total + item?.quantity, 0);
  };

  // Auto-hide cart on mobile when scrolling
  useEffect(() => {
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsCartVisible(currentScrollY < lastScrollY || currentScrollY < 100);
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeaderNav 
        isAuthenticated={false}
        cartCount={getTotalCartItems()}
      />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EventHero 
          event={eventData}
          onShare={handleShare}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <EventDescription event={eventData} />
            
            <VenueInfo venue={eventData?.venue} />
            
            <TicketTiers 
              tickets={ticketData}
              onAddToCart={(ticket, quantity) => handleAddToCart({...ticket, type: 'ticket'}, quantity)}
              cartItems={cartItems}
            />
            
            <UpgradesSection 
              upgrades={upgradeData}
              onAddToCart={(upgrade, quantity) => handleAddToCart({...upgrade, type: 'upgrade'}, quantity)}
              cartItems={cartItems}
            />
            
            <MerchandiseSection 
              merchandise={merchandiseData}
              onAddToCart={(item, quantity) => handleAddToCart({...item, type: 'merchandise'}, quantity)}
              cartItems={cartItems}
            />
          </div>
          
          {/* Desktop Cart Sidebar - Hidden on mobile */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              {cartItems?.length > 0 && (
                <div className="bg-card rounded-lg p-6 shadow-elevation-2">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Order Summary</h3>
                  
                  <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                    {cartItems?.map((item, index) => (
                      <div key={`${item?.id}-${item?.type}-${index}`} className="flex justify-between text-sm">
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{item?.name}</p>
                          <div className="text-muted-foreground">
                            <span className="capitalize">{item?.type}</span>
                            {item?.selectedSize && ` • Size ${item?.selectedSize}`}
                            <span> • Qty: {item?.quantity}</span>
                          </div>
                        </div>
                        <div className="text-foreground font-medium">
                          ${(item?.price * item?.quantity)?.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-border pt-4 mb-6">
                    <div className="flex justify-between text-lg font-semibold text-foreground">
                      <span>Total</span>
                      <span>
                        ${cartItems?.reduce((total, item) => total + (item?.price * item?.quantity), 0)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCheckout}
                    className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Proceed to Checkout</span>
                    <Icon name="ArrowRight" size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Sticky Cart for Mobile/Desktop */}
      <StickyCart 
        cartItems={cartItems}
        onCheckout={handleCheckout}
        onToggleCart={() => setIsCartVisible(!isCartVisible)}
        isVisible={isCartVisible}
      />
      {/* Share Modal */}
      <ShareModal 
        event={eventData}
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </div>
  );
};

export default EventDetail;